import React from "react"
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible"
import type { CollapsibleRootChangeEventDetails } from "@base-ui/react/collapsible"
import { CollapsibleTrigger } from "./collapsible-trigger"
import { CollapsibleContent } from "./collapsible-content"
import { CollapsibleChevron } from "./collapsible-chevron"

export type { CollapsibleTriggerProps } from "./collapsible-trigger"
export type { CollapsibleContentProps } from "./collapsible-content"
export type { CollapsibleChevronProps } from "./collapsible-chevron"
export type { CollapsibleRootChangeEventDetails as CollapsibleChangeEventDetails } from "@base-ui/react/collapsible"

// =============================================================================
// Props
// =============================================================================
export type CollapsibleProps = {
  /** A `Collapsible.Trigger` followed by a `Collapsible.Content` */
  children?: React.ReactNode
  /** Additional CSS classes, such as a border between sections */
  className?: string
  /** Whether the content is shown (controlled). Pair with `onOpenChange` */
  open?: boolean
  /** Whether the content starts shown, when left uncontrolled */
  defaultOpen?: boolean
  /** Called when the trigger opens or closes the content */
  onOpenChange?: (
    open: boolean,
    eventDetails: CollapsibleRootChangeEventDetails
  ) => void
  disabled?: boolean
}

// =============================================================================
// Root
// =============================================================================
/**
 * A section whose content is shown and hidden by a trigger, with the height
 * animated in between.
 *
 * Compose with `Collapsible.Trigger` and `Collapsible.Content`, and
 * `Collapsible.Chevron` inside a custom trigger.
 *
 * @example
 * ```tsx
 * <Collapsible defaultOpen>
 *   <Collapsible.Trigger>Typography</Collapsible.Trigger>
 *   <Collapsible.Content>{children}</Collapsible.Content>
 * </Collapsible>
 * ```
 */
function CollapsibleRoot({
  children,
  className,
  open,
  defaultOpen,
  onOpenChange,
  disabled
}: CollapsibleProps) {
  return (
    <CollapsiblePrimitive.Root
      data-slot="collapsible"
      className={className}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      disabled={disabled}
    >
      {children}
    </CollapsiblePrimitive.Root>
  )
}

CollapsibleRoot.displayName = "Collapsible"

// =============================================================================
// Compound component
// =============================================================================
interface CollapsibleComponent {
  (props: CollapsibleProps): React.JSX.Element
  Trigger: typeof CollapsibleTrigger
  Content: typeof CollapsibleContent
  Chevron: typeof CollapsibleChevron
}

export const Collapsible: CollapsibleComponent = Object.assign(
  CollapsibleRoot,
  {
    Trigger: CollapsibleTrigger,
    Content: CollapsibleContent,
    Chevron: CollapsibleChevron
  } as const
)
